import api from 'api';
import classNames from 'classnames';
import ResolveMultiSelectField from 'components/ResolveMultiSelectField';
import useCallbackRef from 'hooks/useCallbackRef';
import { Contact, AddedContactToDeals } from 'models/Contact';
import { Button, Loader } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import validations from 'validations';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';

import './ContactsManageDrawer.styles.scss';

export type ManageDrawerRefProps = SideDrawerRef & {
  setContacts: (contacts: Contact[]) => void;
};

type ContactsManageDrawerProps = {
  className?: string;
  onClose?: () => void;
};

const ContactsManageDrawer: React.ForwardRefRenderFunction<
  ManageDrawerRefProps,
  ContactsManageDrawerProps
> = (props, ref) => {
  const { className } = props;

  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deals, setDeals] = useState<AddedContactToDeals[]>([]);
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => ({ ...drawer, setContacts }), [drawer]);

  const contactsIds = useMemo(() => contacts.map(({ _id }) => _id), [contacts]);

  const classes = classNames('contacts-manage-drawer', className);
  const { t } = useTranslation();

  const dealRoomIdsOptions = useMemo(
    () =>
      deals.map(({ dealRoomId, dealRoomName, state }) => ({
        value: dealRoomId,
        label: dealRoomName,
        disabled: state === 'on',
        state,
      })),
    [deals],
  );

  const initialValues = useMemo(
    () => ({
      dealRoomIds: deals
        .filter(({ state }) => state === 'on')
        .map(({ dealRoomId }) => dealRoomId),
    }),
    [deals],
  );

  const handleOnClose = useCallback(() => drawer?.setOpen(false), [drawer]);

  const handleOnSubmit = useCallback(
    async (values) => {
      if (!values.dealRoomIds.length) {
        handleOnClose?.();
        return;
      }

      try {
        await api.contacts.addContactsToDeals(contactsIds, values.dealRoomIds);
        popSuccess(
          t('successfullyAddedContactsToDeals', {
            count: values.dealRoomIds.length,
          }),
        );
        handleOnClose?.();
      } catch (e) {
        popServerError(e);
      }
    },
    [contactsIds, handleOnClose, popServerError, popSuccess, t],
  );

  useEffect(() => {
    (async () => {
      if (!drawer?.open) return;

      try {
        setIsLoading(true);
        const { data } = await api.contacts.getAvailableDeals(contactsIds);
        setDeals(data);
      } catch (e) {
        popServerError(e);
        handleOnClose?.();
      } finally {
        setIsLoading(false);
      }
    })();
  }, [contactsIds, drawer?.open, handleOnClose, popServerError]);

  return (
    <SideDrawer ref={drawerRef} className={classes}>
      {isLoading ? (
        <Loader />
      ) : (
        <Form
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
          render={({ handleSubmit, submitting, invalid }) => {
            return (
              <form onSubmit={handleSubmit}>
                <h1>
                  {t('whatDealsToAddContacts', { count: contactsIds.length })}
                </h1>

                {deals.length ? (
                  <>
                    <div className="white-panel">
                      <Field
                        name="dealRoomIds"
                        searchable
                        hasCheckbox
                        hasSelectedOptions={false}
                        className="contacts-manage-drawer__multi-select"
                        component={ResolveMultiSelectField}
                        options={dealRoomIdsOptions}
                        validate={validations.requireOneFromArray()}
                      />
                    </div>

                    <Button type="submit" disabled={submitting || invalid}>
                      {t('confirm')}
                    </Button>
                  </>
                ) : (
                  <p>{t('youDontHaveDeals')}</p>
                )}

                {submitting && <Loader />}
              </form>
            );
          }}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(ContactsManageDrawer);
